/**
 * Polls Postgres for a generated fax PDF link with exponential backoff, then
 * navigates to the final-document page. PG miss keeps polling — no Firestore.
 *
 * @module getFaxPdfLinkRecursively
 */
import { MutableRefObject } from "react";
import { NavigateFunction } from "react-router-dom";
import { singleOrderDetailsGenericCallForPostgres } from "../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { SingleOrderDetailsTableName } from "../enums/singleOrderDetailsTableName";
import { SubmissionDataResponse } from "../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { logError } from "./customLogger";

interface GetFaxPdfLinkRecursivelyParams {
  orderId: string;
  /** Tenant org id for the PG submission read (RLS). */
  orgId?: string | null;
  navigate: NavigateFunction;
  intervalRef: MutableRefObject<NodeJS.Timeout | null>;
  setIsSubmitting: (value: boolean) => void;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
}

/**
 * Clears any in-flight fax-PDF poll timeout stored on {@link intervalRef}.
 *
 * @param intervalRef - Ref holding the active `setTimeout` id
 */
export const clearFaxPdfPoller = (
  intervalRef: MutableRefObject<NodeJS.Timeout | null>,
): void => {
  if (intervalRef.current) {
    clearTimeout(intervalRef.current);
    intervalRef.current = null;
  }
};

/**
 * Extracts `filled_pdf_link` from a PG `submission_output` JSONB value.
 *
 * Exported because it is also the refresh-safe source of the filled fax PDF:
 * `location.state.faxPdfLink` only survives the navigation that sets it, so
 * viewers re-read the same link from `submission.submission_output`.
 *
 * @param submissionOutput - Raw `submission.submission_output` (object or JSON string)
 * @returns The filled PDF storage path, or undefined when missing
 */
export const filledPdfLinkFromSubmissionOutput = (
  submissionOutput: unknown,
): string | undefined => {
  let output = submissionOutput;
  if (typeof output === "string") {
    try {
      output = JSON.parse(output);
    } catch {
      return undefined;
    }
  }
  if (!output || typeof output !== "object") {
    return undefined;
  }
  const link = (output as { filled_pdf_link?: unknown }).filled_pdf_link;
  return typeof link === "string" && link.trim() ? link : undefined;
};

/**
 * Resolves `filled_pdf_link` from Postgres `submission.submission_output` only.
 *
 * A missing link is treated as "not ready yet" (caller should keep polling) —
 * not as a signal to fall back to Firestore.
 *
 * @param orderId - Medical PA order id
 * @param orgId - Tenant org id for the PG read (required)
 * @returns The filled PDF path when present
 * @throws {Error} When orgId is missing or the PG read fails
 */
const fetchFilledPdfLinkFromPg = async (
  orderId: string,
  orgId?: string | null,
): Promise<string | undefined> => {
  if (!orgId) {
    throw new Error("orgId is required to poll filled_pdf_link from Postgres");
  }

  const response =
    await singleOrderDetailsGenericCallForPostgres<SubmissionDataResponse>({
      order_id: orderId,
      org_id: orgId,
      table_name: SingleOrderDetailsTableName.SUBMISSION,
    });

  return filledPdfLinkFromSubmissionOutput(
    response?.submission?.submission_output,
  );
};

/**
 * Polls Postgres for a generated fax PDF link using exponential backoff.
 *
 * Reads `submission.submission_output.filled_pdf_link` (written by the fax
 * submit path in PR #4343). An empty/missing value is a poll miss — keep
 * polling PG; do not fall back to Firestore.
 *
 * Clears any previously running poll via {@link intervalRef}, then schedules
 * recursive `setTimeout` calls. Stops and navigates once a link appears.
 * In-flight ticks that finish after {@link clearFaxPdfPoller} (or a newer poll
 * start) are ignored so they cannot navigate after teardown.
 *
 * @param params - Order/org ids, navigation, poller ref, and backoff options
 */
export const getFaxPdfLinkRecursively = ({
  orderId,
  orgId,
  navigate,
  intervalRef,
  setIsSubmitting,
  initialDelayMs = 500,
  maxDelayMs = 8000,
  backoffMultiplier = 2,
}: GetFaxPdfLinkRecursivelyParams): void => {
  clearFaxPdfPoller(intervalRef);

  const poll = (delay: number) => {
    const timeoutId = setTimeout(async () => {
      try {
        const link = await fetchFilledPdfLinkFromPg(orderId, orgId);
        console.log("link", link);

        // Cancelled or superseded while the request was in flight.
        if (intervalRef.current !== timeoutId) {
          return;
        }

        if (link) {
          clearFaxPdfPoller(intervalRef);
          navigate(`/worklists/final-document/${orderId}`, {
            state: { faxPdfLink: link },
          });
          setIsSubmitting(false);
          return;
        }

        // PG miss = not ready yet — keep polling PG, no Firestore fallback.
        const nextDelay = Math.min(delay * backoffMultiplier, maxDelayMs);

        poll(nextDelay);
      } catch (error) {
        logError(error as Error, "Error fetching fax PDF link from Postgres");
        if (intervalRef.current === timeoutId) {
          clearFaxPdfPoller(intervalRef);
          setIsSubmitting(false);
        }
      }
    }, delay);

    intervalRef.current = timeoutId;
  };

  poll(initialDelayMs);
};
